import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { workspaceService } from "../lib/services";
import type { WorkspaceWithMembership } from "../lib/types";

const STORAGE_KEY = "showrunner:lastWorkspaceId";

interface WorkspaceContextValue {
  workspaces: WorkspaceWithMembership[];
  activeWorkspace: WorkspaceWithMembership | null;
  loading: boolean;
  error: string | null;
  refetchWorkspaces: () => Promise<void>;
  removeWorkspace: (workspaceId: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const { workspaceId: urlWorkspaceId } = useParams<{ workspaceId: string }>();

  const [workspaces, setWorkspaces] = useState<WorkspaceWithMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkspaces = useCallback(async () => {
    if (!session) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await workspaceService.list();
      setWorkspaces(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load workspaces");
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  // Determine active workspace: URL param > localStorage > first available
  const activeWorkspace = useMemo((): WorkspaceWithMembership | null => {
    if (workspaces.length === 0) return null;
    if (urlWorkspaceId) {
      const match = workspaces.find((w) => w.id === urlWorkspaceId);
      if (match) return match;
    }
    const lastId = localStorage.getItem(STORAGE_KEY);
    if (lastId) {
      const match = workspaces.find((w) => w.id === lastId);
      if (match) return match;
    }
    return workspaces[0] ?? null;
  }, [workspaces, urlWorkspaceId]);

  // Persist active workspace to localStorage
  useEffect(() => {
    if (activeWorkspace) {
      localStorage.setItem(STORAGE_KEY, activeWorkspace.id);
    }
  }, [activeWorkspace]);

  const removeWorkspace = useCallback((workspaceId: string) => {
    setWorkspaces((prev) => prev.filter((w) => w.id !== workspaceId));
  }, []);

  const value = useMemo(
    () => ({
      workspaces,
      activeWorkspace,
      loading,
      error,
      refetchWorkspaces: fetchWorkspaces,
      removeWorkspace,
    }),
    [workspaces, activeWorkspace, loading, error, fetchWorkspaces, removeWorkspace],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return ctx;
}
